import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState } from 'react';
import { X, Phone, Calendar, Clock, CreditCard, User, FileText, CheckCircle, XCircle, Repeat, TrendingUp, RefreshCw, AlertCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import BookingActivityTimeline from './BookingActivityTimeline';
import { RecurringBooking } from '../../types/bookings';
import {
  formatDate,
  formatTime,
  formatDateTimeString,
  formatCurrency,
  getStatusColor,
  getPaymentStatusColor,
  getRefundStatusColor,
  getRefundStatusLabel,
  formatFrequency,
  calculateProgress,
} from '../../utils/bookingHelpers';

interface RecurringBookingDetailsModalProps {
  booking: RecurringBooking | null;
  isOpen: boolean;
  onClose: () => void;
  onCheckInNext?: (instanceId: number) => void;
  onRescheduleInstance?: (instanceId: number) => void;
  onCancelInstance?: (instanceId: number) => void;
  onCancelFuture?: (bookingId: number) => void;
  onCancelAll?: (bookingId: number) => void;
}

export default function RecurringBookingDetailsModal({
  booking,
  isOpen,
  onClose,
  onCheckInNext,
  onRescheduleInstance,
  onCancelInstance,
  onCancelFuture,
  onCancelAll,
}: RecurringBookingDetailsModalProps) {
  const [isCheckingIn, setIsCheckingIn] = useState(false);

  // Early return if no booking - must be after all hooks
  if (!booking) {
    return null;
  }

  const progress = calculateProgress(booking.completed_sessions || 0, booking.total_sessions || 0);

  // Find next session manually
  const today = new Date().toISOString().split('T')[0];
  const nextSession = booking.instances
    ?.filter(inst => inst.scheduled_date >= today && inst.status !== 'cancelled')
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0];

  const statusColor = getStatusColor(booking.status);
  const paymentColor = getPaymentStatusColor(booking.payment_status);

  const courtName = booking.court?.name || 'N/A';
  const sportName = booking.court?.sport?.name || 'N/A';
  const customerName = booking.customer?.name || booking.user?.name || 'N/A';
  const customerPhone = booking.customer?.phone || booking.user?.phone;
  const customerEmail = booking.customer?.email || booking.user?.email;

  const handleCheckInNext = async () => {
    if (!onCheckInNext || !nextSession) return;

    setIsCheckingIn(true);
    try {
      await onCheckInNext(nextSession.id);
      onClose();
    } catch (error) {
      console.error('Check-in failed:', error);
    } finally {
      setIsCheckingIn(false);
    }
  };

  // Check if any instance has been rescheduled
  const rescheduledInstances = booking.instances?.filter(inst => inst.is_rescheduled) || [];
  const hasRescheduledSessions = rescheduledInstances.length > 0;

  // Count cancelled sessions
  const cancelledInstances = booking.instances?.filter(inst => inst.status === 'cancelled') || [];
  const cancelledCount = cancelledInstances.length;
  const totalSessions = booking.total_sessions || 0;
  const hasPartiallyCancelledSessions = cancelledCount > 0 && cancelledCount < totalSessions;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" maxHeight="max-h-[95vh] sm:max-h-[90vh]" animate={false}>
      {/* Header */}
      <div className="sticky top-0 bg-blue-50 border-b border-blue-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 sm:gap-3">
            <Repeat className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-blue-900">Recurring Booking</h2>
              <p className="text-xs sm:text-sm text-blue-700">{booking.booking_reference}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Status Badges */}
          <div className="flex gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold ${statusColor}`}>
              {booking.status.replace('_', ' ').toUpperCase()}
            </span>
            <span className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold ${paymentColor}`}>
              {booking.payment_status.replace('_', ' ').toUpperCase()}
            </span>
            <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-purple-100 text-purple-800 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              {formatFrequency(booking.frequency, booking.days_of_week)}
            </span>
            {/* Partial Cancellation Badge */}
            {hasPartiallyCancelledSessions && (
              <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-red-100 text-red-800 border border-red-200 flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                {cancelledCount} of {totalSessions} sessions cancelled
              </span>
            )}
          </div>

          {/* Progress Bar */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 sm:p-4 border border-blue-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 sm:mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm sm:text-base">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                Progress
              </h3>
              <span className="text-xs sm:text-sm text-gray-600">
                {booking.completed_sessions || 0} / {booking.total_sessions || 0} sessions
              </span>
            </div>
            <div className="w-full bg-white rounded-full h-2.5 sm:h-3 overflow-hidden shadow-inner">
              <div
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-2">{progress}% completed</p>
          </div>

          {/* Customer Info */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
            <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Customer Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Name</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{customerName}</p>
              </div>
              {customerEmail && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Email</p>
                  <a
                    href={`mailto:${customerEmail}`}
                    className="text-blue-600 hover:text-blue-700 font-semibold text-xs sm:text-sm break-all"
                  >
                    {customerEmail}
                  </a>
                </div>
              )}
              {customerPhone && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Phone</p>
                  <a
                    href={`tel:${customerPhone}`}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1.5 font-semibold text-sm sm:text-base"
                  >
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {customerPhone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
            <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Booking Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Court</p>
                <p className="text-base font-semibold text-gray-900">{courtName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Sport</p>
                <p className="text-base font-semibold text-gray-900">{sportName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Frequency</p>
                <p className="text-base font-semibold text-gray-900">{formatFrequency(booking.frequency, booking.days_of_week)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Duration</p>
                <p className="text-base font-semibold text-gray-900">{booking.duration_months} month(s)</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Start Date</p>
                <p className="text-base font-semibold text-gray-900">{formatDate(booking.start_date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">End Date</p>
                <p className="text-base font-semibold text-gray-900">{formatDate(booking.end_date)}</p>
              </div>
            </div>
          </div>

          {/* Next Session Highlight */}
          {nextSession && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700 font-semibold">Next Session</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatDate(nextSession.scheduled_date)} • {formatTime(nextSession.scheduled_start_time)} - {formatTime(nextSession.scheduled_end_time)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Info */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
            <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              Payment Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Total Amount</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{formatCurrency(booking.final_price)}</p>
              </div>
              {booking.discount_applied && booking.discount_applied > 0 && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Discount Applied</p>
                  <p className="text-green-600 font-semibold text-sm sm:text-base">-{formatCurrency(booking.discount_applied)}</p>
                </div>
              )}
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Payment Status</p>
                <p className="text-gray-900 font-semibold capitalize text-sm sm:text-base">
                  {booking.payment_status.replace('_', ' ')}
                </p>
              </div>
              {booking.paid_at && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Paid At</p>
                  <p className="text-gray-900 font-semibold text-xs sm:text-sm">{formatDateTimeString(booking.paid_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Vendor Revenue Split */}
          {booking.venue_share && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4 sm:p-5">
              <h3 className="font-bold text-green-900 mb-3 sm:mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Venue Revenue
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-white rounded-xl p-3 sm:p-4">
                  <p className="text-sm text-green-600 mb-1">Your Share</p>
                  <p className="text-green-900 font-black text-2xl sm:text-3xl">{formatCurrency(booking.venue_share)}</p>
                </div>
                {booking.platform_commission && (
                  <div className="bg-white rounded-xl p-3 sm:p-4">
                    <p className="text-sm text-gray-600 mb-1">Platform Fee</p>
                    <p className="text-gray-900 font-bold text-lg sm:text-xl">{formatCurrency(booking.platform_commission)}</p>
                  </div>
                )}
                <div className="pt-3 border-t-2 border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700 font-semibold">Total Amount</span>
                    <span className="text-green-900 font-bold text-lg sm:text-xl">{formatCurrency(booking.final_price)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reschedule History */}
          {hasRescheduledSessions && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-blue-900 mb-3 sm:mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Reschedule History
              </h4>
              <div className="space-y-3">
                {rescheduledInstances.map((instance, idx) => (
                  <div key={instance.id}>
                    <div className="bg-white rounded-xl p-3 sm:p-4">
                      <p className="text-sm text-blue-600 font-semibold mb-2">Session #{instance.instance_number}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500">Original Date</p>
                          <p className="text-gray-900 font-semibold">{instance.original_date ? formatDate(instance.original_date) : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">New Date</p>
                          <p className="text-gray-900 font-semibold">{formatDate(instance.scheduled_date)}</p>
                        </div>
                      </div>
                    </div>
                    {instance.cancellation_reason && (
                      <div className="bg-blue-100 rounded-xl p-3 mt-2">
                        <p className="text-blue-600 font-semibold text-xs">Reason:</p>
                        <p className="text-blue-800 text-xs">{instance.cancellation_reason}</p>
                      </div>
                    )}
                    {idx < rescheduledInstances.length - 1 && (
                      <div className="flex items-center justify-center my-2">
                        <div className="w-full border-t-2 border-dashed border-blue-300"></div>
                        <span className="px-3 text-blue-600 font-bold">→</span>
                        <div className="w-full border-t-2 border-dashed border-blue-300"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cancellation Details */}
          {booking.status === 'cancelled' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-red-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                Cancellation Details
              </h4>
              <div className="space-y-3 sm:space-y-4">
                {/* Inline Details */}
                <div className="bg-white rounded-xl p-3 sm:p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {booking.cancelled_by_type && (
                      <div>
                        <p className="text-xs sm:text-sm text-red-600 mb-1">Cancelled By</p>
                        <p className="text-red-900 font-bold capitalize text-sm sm:text-base">{booking.cancelled_by_type}</p>
                      </div>
                    )}
                    {booking.cancelled_at && (
                      <div>
                        <p className="text-xs sm:text-sm text-red-600 mb-1">Cancellation Date</p>
                        <p className="text-red-900 font-bold text-xs sm:text-sm">{formatDateTimeString(booking.cancelled_at)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Refund Information */}
                {booking.refund_amount && booking.refund_amount > 0 && (
                  <div className="bg-white rounded-xl p-3 sm:p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Refund Amount</p>
                        <p className="text-gray-900 font-bold text-base sm:text-lg">{formatCurrency(booking.refund_amount)}</p>
                      </div>
                      {booking.refund_status && (
                        <div>
                          <p className="text-xs sm:text-sm text-gray-600 mb-1">Refund Status</p>
                          <span className={`inline-flex px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold ${getRefundStatusColor(booking.refund_status)}`}>
                            {getRefundStatusLabel(booking.refund_status)}
                          </span>
                        </div>
                      )}
                    </div>
                    {booking.refund_status === 'processed' && (
                      <p className="text-xs text-gray-500 mt-2 sm:mt-3">
                        Refund processed successfully. Funds will appear in customer's account within 5-7 business days.
                      </p>
                    )}
                    {booking.refund_status === 'processing' && (
                      <p className="text-xs text-blue-600 mt-2 sm:mt-3">
                        Refund is being processed by Paystack. This typically takes 1-2 business days.
                      </p>
                    )}
                    {booking.refund_status === 'pending' && (
                      <p className="text-xs text-yellow-600 mt-2 sm:mt-3">
                        Refund is pending. Will be processed shortly.
                      </p>
                    )}
                  </div>
                )}

                {/* Reason */}
                {booking.cancellation_reason && (
                  <div className="bg-white rounded-xl p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-red-600 mb-2 font-semibold">Cancellation Reason</p>
                    <p className="text-red-900 text-sm sm:text-base">{booking.cancellation_reason}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* All Sessions List - Swipeable Horizontal */}
          {booking.instances && booking.instances.length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 sm:p-5">
              <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                All Sessions ({booking.instances.length})
              </h4>

              {/* Swipeable horizontal scroll container */}
              <div className="overflow-x-auto scrollbar-hide -mx-4 sm:-mx-5 px-4 sm:px-5">
                <div className="flex gap-2 sm:gap-3 pb-2">
                  {booking.instances.map((instance, index) => {
                    const instanceStatusColor = getStatusColor(instance.status);
                    const isCancelled = instance.status === 'cancelled';
                    const isCompleted = instance.status === 'completed';
                    const isScheduled = ['scheduled', 'confirmed'].includes(instance.status);

                    // Check if instance can be cancelled or rescheduled
                    const instanceDateTime = new Date(`${instance.scheduled_date}T${instance.scheduled_start_time}`);
                    const now = new Date();
                    // Vendors can cancel any scheduled/confirmed session (past or future)
                    const canCancelInstance = ['scheduled', 'confirmed'].includes(instance.status);
                    // Rescheduling only makes sense for future sessions
                    const canRescheduleInstance = ['scheduled', 'confirmed'].includes(instance.status) && instanceDateTime > now;

                    return (
                      <div
                        key={instance.id}
                        className={`min-w-[280px] rounded-xl border-2 p-4 transition-all ${
                          isCancelled ? 'bg-red-50 border-red-200' :
                          isCompleted ? 'bg-green-50 border-green-200' :
                          isScheduled ? 'bg-blue-50 border-blue-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        {/* Session Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              isCancelled ? 'bg-red-200 text-red-700' :
                              isCompleted ? 'bg-green-200 text-green-700' :
                              'bg-blue-200 text-blue-700'
                            }`}>
                              #{index + 1}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${instanceStatusColor}`}>
                              {instance.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>

                          {/* Action buttons for scheduled instances */}
                          <div className="flex gap-2">
                            {/* Reschedule button */}
                            {canRescheduleInstance && onRescheduleInstance && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRescheduleInstance(instance.id);
                                }}
                                className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg flex items-center gap-1.5 transition-colors text-xs font-semibold"
                                title="Reschedule this session"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Reschedule
                              </button>
                            )}

                            {/* Cancel button */}
                            {canCancelInstance && onCancelInstance && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelInstance(instance.id);
                                }}
                                className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg flex items-center gap-1.5 transition-colors text-xs font-semibold"
                                title="Cancel this session"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Session Details */}
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Date</p>
                            <p className="text-sm font-bold text-gray-900">
                              {formatDate(instance.scheduled_date)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Time</p>
                            <p className="text-sm font-bold text-gray-900">
                              {formatTime(instance.scheduled_start_time)} - {formatTime(instance.scheduled_end_time)}
                            </p>
                          </div>
                          {instance.checked_in_at && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Checked In</p>
                              <p className="text-xs text-gray-900">
                                {formatDateTimeString(instance.checked_in_at)}
                              </p>
                            </div>
                          )}

                          {/* Cancellation Info for Cancelled Instances */}
                          {isCancelled && instance.cancelled_at && (
                            <div className="border-t border-red-200 pt-2 mt-2">
                              <p className="text-xs text-red-600 mb-1">Cancelled At</p>
                              <p className="text-xs text-red-900 font-semibold">
                                {formatDateTimeString(instance.cancelled_at)}
                              </p>
                            </div>
                          )}

                          {/* Cancellation Reason */}
                          {isCancelled && instance.cancellation_reason && (
                            <div className="bg-red-100 rounded-lg p-2 mt-2">
                              <p className="text-xs text-red-600 font-semibold mb-1">Reason:</p>
                              <p className="text-xs text-red-900">{instance.cancellation_reason}</p>
                            </div>
                          )}

                          {/* Refund Information for Cancelled Instances */}
                          {isCancelled && instance.refund_amount && instance.refund_amount > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-2">
                              <p className="text-xs text-green-700 font-semibold mb-1">Refund Processed</p>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-green-600">Amount:</span>
                                <span className="text-sm font-bold text-green-900">
                                  {CURRENCY_SYMBOL}{Number(instance.refund_amount).toLocaleString()}
                                </span>
                              </div>
                              {instance.refund_gateway_fee_paid_by && (
                                <p className="text-xs text-green-600 mt-1">
                                  Gateway fee paid by: <span className="font-semibold capitalize">{instance.refund_gateway_fee_paid_by}</span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {booking.instances.length > 3 && (
                <p className="text-xs text-gray-500 mt-3 text-center">← Swipe to see all sessions →</p>
              )}
            </div>
          )}

          {/* Per-booking history (activity.view) — spans the group + its sessions */}
          <BookingActivityTimeline venueId={booking.venue_id} recurringBookingId={booking.id} />
        </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row gap-3">
          {nextSession && booking.status === 'active' && onCheckInNext && (
            <button
              onClick={handleCheckInNext}
              disabled={isCheckingIn}
              className="w-full sm:flex-1 bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              {isCheckingIn ? 'Checking In...' : 'Check In Next Session'}
            </button>
          )}

          {booking.status === 'active' && (booking.remaining_sessions || 0) > 0 && onCancelAll && (
            <button
              onClick={() => onCancelAll(booking.id)}
              className="w-full sm:flex-1 bg-red-50 text-red-700 py-3 rounded-xl font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
              title="Cancel all remaining sessions"
            >
              <XCircle className="w-4 h-4" />
              Cancel All Remaining Sessions
            </button>
          )}

          <button
            onClick={onClose}
            className="w-full sm:w-auto bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors px-8"
          >
            Close
          </button>
        </div>
    </Modal>
  );
}
